import { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../lib/api';
import { useAssignRange, useAssignSite, useRemoveAssignment } from '../../lib/queries';
import { addDays, formatDate, formatWeekday } from '../../lib/dates';
import { ABSENCE_LABELS, absenceReason } from '../../lib/labels';
import type { Crew, Employee } from '../../lib/types';
import { otherSitesOnDay, type DayColumn, type SiteTile } from './planModel';

interface SiteDayDialogProps {
  tile: SiteTile;
  column: DayColumn;
  crews: Crew[];
  employees: Employee[];
  canEdit: boolean;
  onClose: () => void;
}

/**
 * Besetzung einer Baustelle an einem Tag.
 *
 * Angehakt wird, wer arbeiten soll — nach Kolonnen gegliedert, weil im Büro in
 * Kolonnen gedacht wird. Abwesende bleiben sichtbar, aber gesperrt: sie
 * wortlos auszublenden würde die Frage „wo ist der eigentlich?" offenlassen.
 */
export function SiteDayDialog({
  tile,
  column,
  crews,
  employees,
  canEdit,
  onClose,
}: SiteDayDialogProps) {
  const toast = useToast();
  const assign = useAssignSite();
  const assignRange = useAssignRange();
  const removeAssignment = useRemoveAssignment();

  const day = column.date;
  const [untilDay, setUntilDay] = useState(day);

  const entries = tile.entries;
  const absentById = new Map(column.absent.map((item) => [item.employee.id, item.absence]));

  const groups = useMemo(() => groupByCrew(employees, crews), [employees, crews]);
  const isRange = untilDay > day;

  const fail = (error: unknown, fallback: string) =>
    toast.error(error instanceof ApiError ? error.message : fallback);

  const add = (employeeId: number) => {
    if (isRange) {
      assignRange.mutate(
        {
          employeeIds: [employeeId],
          siteId: tile.site.id,
          from: day,
          to: untilDay,
          includeWeekends: false,
          skipAbsent: true,
          replace: false,
        },
        {
          onSuccess: (result) =>
            toast.success(
              `${result.created} Tage eingeteilt${
                result.skippedAbsent ? `, ${result.skippedAbsent} wegen Abwesenheit ausgelassen` : ''
              }.`,
            ),
          onError: (error) => fail(error, 'Einteilung fehlgeschlagen.'),
        },
      );
      return;
    }

    assign.mutate(
      { employeeId, siteId: tile.site.id, date: day },
      {
        onSuccess: (result) => {
          if (result.warnings.length > 0) toast.warn('Eingeteilt', result.warnings.join(' '));
        },
        onError: (error) => fail(error, 'Einteilung fehlgeschlagen.'),
      },
    );
  };

  const remove = (assignmentId: number) => {
    removeAssignment.mutate(assignmentId, {
      onError: (error) => fail(error, 'Austragen fehlgeschlagen.'),
    });
  };

  /**
   * Eine ganze Kolonne in einem Rutsch einteilen. Abwesende werden ausgelassen,
   * schon Eingeteilte nicht doppelt eingetragen — das regelt der Server.
   */
  const addCrew = (group: CrewGroup) => {
    if (!group.crew) return;
    const crewName = group.crew.name;
    assignRange.mutate(
      {
        employeeIds: group.members.map((member) => member.id),
        siteId: tile.site.id,
        from: day,
        to: untilDay,
        // Ein einzelner Tag zählt immer (auch Sa/So) — ein Zeitraum bleibt
        // werktags, wie es der Hinweis über der Liste verspricht.
        includeWeekends: !isRange,
        skipAbsent: true,
        replace: false,
      },
      {
        onSuccess: (result) =>
          toast.success(
            `Kolonne "${crewName}": ${result.created} Einteilung(en)${
              result.skippedAbsent ? `, ${result.skippedAbsent} wegen Abwesenheit ausgelassen` : ''
            }.`,
          ),
        onError: (error) => fail(error, 'Einteilung fehlgeschlagen.'),
      },
    );
  };

  return (
    <Modal
      title={tile.site.name}
      subtitle={`${formatWeekday(day)}, ${formatDate(day)} · ${entries.length} eingeteilt`}
      onClose={onClose}
      width="breit"
      className="sdd-modal"
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Fertig
        </button>
      }
    >
      {canEdit ? (
        <div className="sdd-range">
          <label htmlFor="sdd-until">Übernehmen bis</label>
          <input
            id="sdd-until"
            type="date"
            className="input"
            value={untilDay}
            min={day}
            max={addDays(day, 90)}
            onChange={(event) => setUntilDay(event.target.value || day)}
          />
          <span className="muted">
            {isRange
              ? 'Neue Haken gelten für alle Werktage bis dahin. Abwesende werden ausgelassen.'
              : 'Nur dieser Tag.'}
          </span>
        </div>
      ) : null}

      <div className="sdd-groups">
        {groups.map((group) => (
          <section key={group.crew?.id ?? 'ohne'} className="sdd-group">
            <div className="sdd-group-head">
              <h3 style={{ '--crew': group.crew?.color ?? 'var(--line-strong)' } as never}>
                {group.crew?.name ?? 'Ohne Kolonne'}
              </h3>
              {canEdit && group.crew ? (
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() => addCrew(group)}
                  disabled={assignRange.isPending}
                >
                  Ganze Kolonne einteilen
                </button>
              ) : null}
            </div>

            <ul className="sdd-list">
              {group.members.map((employee) => {
                const entry = entries.find((item) => item.employee.id === employee.id);
                const absence = absentById.get(employee.id);
                const elsewhere = otherSitesOnDay(column, employee.id, tile.site.id);

                return (
                  <li key={employee.id}>
                    <label
                      className={`sdd-item${entry ? ' is-on' : ''}${absence ? ' is-blocked' : ''}`}
                      title={absence ? `Nicht verfügbar: ${absenceReason(absence)}` : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(entry)}
                        disabled={!canEdit || (!entry && Boolean(absence) && !isRange)}
                        onChange={() => (entry ? remove(entry.assignment.id) : add(employee.id))}
                      />
                      <span className="sdd-item-name">{employee.displayName}</span>
                      {employee.role ? <span className="sdd-item-role">{employee.role}</span> : null}

                      {absence ? (
                        <span className="sdd-item-flag is-absent" title={absenceReason(absence)}>
                          {ABSENCE_LABELS[absence.type]}
                        </span>
                      ) : null}
                      {elsewhere.length > 0 ? (
                        <span className="sdd-item-flag is-elsewhere">auf {elsewhere.join(', ')}</span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}

interface CrewGroup {
  crew: Crew | null;
  members: Employee[];
}

function groupByCrew(employees: Employee[], crews: Crew[]): CrewGroup[] {
  const groups: CrewGroup[] = [];

  for (const crew of crews) {
    const members = employees.filter((employee) => employee.crewId === crew.id);
    if (members.length > 0) groups.push({ crew, members });
  }

  const rest = employees.filter(
    (employee) => employee.crewId === null || !crews.some((crew) => crew.id === employee.crewId),
  );
  if (rest.length > 0) groups.push({ crew: null, members: rest });

  return groups;
}
